'use strict';

/**
 * Pict-Provider-DocSyncAPI
 * ------------------------
 * Browser-side client for Documentation Sync (/1.0/Plansheet/DocSync). Backs the
 * Documentation Sync section of the settings hub: it lists the GitHub App
 * installations, reads and writes the stored repo->docs bindings, and kicks off a
 * sync run. Gated server-side by content.edit.
 */

const libPictProvider = require('pict').ServiceProviderBase;
const libFrameworkClient = require('./FrameworkClient.js');

const _DefaultProviderConfiguration =
{
	ProviderIdentifier: 'DocSyncAPI',
	Dependencies: [],
	RoutePrefix: '/1.0/'
};

class PictProviderDocSyncAPI extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		let tmpOptions = Object.assign({}, _DefaultProviderConfiguration, pOptions || {});
		super(pFable, tmpOptions, pServiceHash);
		this.routePrefix = this.options.RoutePrefix;
	}

	_url(pPath)
	{
		let tmpPrefix = this.routePrefix;
		if (tmpPrefix.charAt(tmpPrefix.length - 1) !== '/') { tmpPrefix += '/'; }
		return tmpPrefix + pPath.replace(/^\//, '');
	}

	// The stored repo->docs bindings for this plan sheet. Returns an array (empty when none).
	getBindings()
	{
		return libFrameworkClient.getJSON(this.fable, this._url('Plansheet/DocSync'))
			.then((pBody) => (pBody && pBody.Bindings) || []);
	}

	// The GitHub App state: { Configured, InstallURL, Installations }. Configured:false means the
	// server has no GitHub App set up, so the section can only explain that.
	listInstallations()
	{
		return libFrameworkClient.getJSON(this.fable, this._url('Plansheet/DocSync/Installations'))
			.then((pBody) => pBody || { Configured: false, InstallURL: '', Installations: [] });
	}

	// Create or update a binding. pPatch is { Github, Ref?, SourcePath?, TargetPath?, Direction?,
	// GithubInstallationID?, Enabled? }. Throws on a non-Success envelope; returns the saved Binding.
	saveBinding(pPatch)
	{
		return libFrameworkClient.putJSON(this.fable, this._url('Plansheet/DocSync'), pPatch || {})
			.then((pBody) =>
			{
				if (!pBody || !pBody.Success) { throw new Error((pBody && pBody.Error) || 'HTTP 200'); }
				return pBody.Binding;
			});
	}

	// Run a sync. pBody is { Github?, Ref?, SourcePath?, TargetPath? }; omit Github to re-run the
	// stored bindings. Throws on a non-Success envelope; returns the Results array.
	runSync(pBody)
	{
		return libFrameworkClient.postJSON(this.fable, this._url('Plansheet/DocSync/Run'), pBody || {})
			.then((pResponse) =>
			{
				if (!pResponse || !pResponse.Success) { throw new Error((pResponse && pResponse.Error) || 'HTTP 200'); }
				return pResponse.Results;
			});
	}

	// --- account-owned GitHub credentials (coexist with the GitHub App) ---

	// The account's stored GitHub tokens: { Configured, Tokens: [...] }. Configured:false means the
	// server has no token-encryption key, so a token cannot be stored (the UI explains that).
	listGithubTokens()
	{
		return libFrameworkClient.getJSON(this.fable, this._url('Account/GithubTokens'))
			.then((pBody) => pBody || { Configured: false, Tokens: [] });
	}

	// Add a token. pBody is { Token, Label?, Kind?, Scope?, Repo? }. The server validates it against
	// GitHub before storing and never echoes the token back. Returns { Token (public row), Probe }.
	addGithubToken(pBody)
	{
		return libFrameworkClient.postJSON(this.fable, this._url('Account/GithubTokens'), pBody || {})
			.then((pResponse) =>
			{
				if (!pResponse || !pResponse.Success) { throw new Error((pResponse && pResponse.Error) || 'HTTP 200'); }
				return pResponse;
			});
	}

	// Re-run the GitHub self-test for a stored token. pBody is { Repo? }. Returns the probe result
	// ({ Ok, Login, RepoCount, Repos, RepoCheck, CanWrite }) and refreshes the stored CanWrite flag.
	testGithubToken(pID, pBody)
	{
		return libFrameworkClient.postJSON(this.fable, this._url('Account/GithubTokens/' + encodeURIComponent(pID) + '/Test'), pBody || {})
			.then((pResponse) =>
			{
				if (!pResponse || !pResponse.Success) { throw new Error((pResponse && pResponse.Error) || 'HTTP 200'); }
				return pResponse;
			});
	}

	// Revoke (soft-delete) a stored token. Any binding pinned to it falls back to the GitHub App.
	revokeGithubToken(pID)
	{
		return libFrameworkClient.delJSON(this.fable, this._url('Account/GithubTokens/' + encodeURIComponent(pID)))
			.then((pResponse) =>
			{
				if (!pResponse || !pResponse.Success) { throw new Error((pResponse && pResponse.Error) || 'HTTP 200'); }
				return pResponse;
			});
	}
}

module.exports = PictProviderDocSyncAPI;
module.exports.default_configuration = _DefaultProviderConfiguration;
